import React from "react";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";

/*
 * =========================================================
 * NOTIFICATION POPUP
 * =========================================================
 *
 * Generic normal-notification popup.
 *
 * Behaviour:
 * - Auto closes after configured duration.
 * - Default duration = 6 seconds.
 * - Hover pauses the countdown.
 * - Mouse leave resumes the countdown.
 * - Expanding pauses the countdown.
 * - Collapsing resumes the countdown.
 * - New notifications added to the group do NOT restart
 *   the existing countdown.
 * - X closes immediately.
 * - Swipe up dismisses the popup.
 * - Dragging slightly follows the pointer for a native feel.
 *
 * Notification visibility/configuration comes from
 * emp.NotificationConfig through emp.GetNotifications.
 */

const DEFAULT_AUTO_CLOSE_MS = 6000;
const SWIPE_DISMISS_THRESHOLD = 70;
const SWIPE_MAX_DRAG = 140;
const SWIPE_CLOSE_ANIMATION_MS = 160;

/*
 * =========================================================
 * HELPERS
 * =========================================================
 */

const toBoolean = (value, fallback = false) => {
  if (value === true || value === false) {
    return value;
  }

  if (Number(value) === 1) {
    return true;
  }

  if (Number(value) === 0) {
    return false;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }

    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
  }

  return fallback;
};

const getConfiguredAutoCloseMs = (notification) => {
  const configuredSeconds = Number(
    notification?.autoCloseSeconds ?? notification?.AutoCloseSeconds,
  );

  /*
   * Only accept a sensible popup lifetime.
   * Bad/huge DB values like 3491 become the 6 second default.
   */
  if (
    !Number.isFinite(configuredSeconds) ||
    configuredSeconds < 1 ||
    configuredSeconds > 60
  ) {
    return DEFAULT_AUTO_CLOSE_MS;
  }

  return configuredSeconds * 1000;
};

const getModuleName = (notification) =>
  String(
    notification?.moduleName ??
      notification?.ModuleName ??
      notification?.moduleCode ??
      notification?.ModuleCode ??
      "Notification",
  ).trim() || "Notification";

const getActionName = (notification) =>
  String(notification?.actionName ?? notification?.ActionName ?? "").trim();

const getMessage = (notification) =>
  String(notification?.message ?? notification?.Message ?? "").trim();

const isPopupEnabled = (notification) =>
  toBoolean(notification?.popupEnabled ?? notification?.PopupEnabled, true) &&
  toBoolean(
    notification?.notificationEnabled ?? notification?.NotificationEnabled,
    true,
  );

/*
 * =========================================================
 * ROOT COMPONENT
 * =========================================================
 */

const EpoNotificationToast = ({
  notification = null,
  notifications = [],
  onClose,
  onActionComplete,
  isNormalGroup = false,
}) => {
  /*
   * AuthProvider uses this component only for the generic
   * notification popup.
   *
   * Approval actions are handled separately inside
   * NotificationDropdown > Approval Required.
   */
  if (!isNormalGroup) {
    return null;
  }

  return (
    <NormalNotificationGroup notifications={notifications} onClose={onClose} />
  );
};

/*
 * =========================================================
 * NORMAL NOTIFICATION GROUP
 * =========================================================
 */

const NormalNotificationGroup = ({ notifications, onClose }) => {
  /*
   * ---------------------------------------------------------
   * UI STATE
   * ---------------------------------------------------------
   */

  const [expanded, setExpanded] = React.useState(false);

  const [isPaused, setIsPaused] = React.useState(false);

  const [swipeOffset, setSwipeOffset] = React.useState(0);

  const [isSwiping, setIsSwiping] = React.useState(false);

  const [isDismissing, setIsDismissing] = React.useState(false);

  /*
   * ---------------------------------------------------------
   * NOTIFICATIONS
   * ---------------------------------------------------------
   */

  const visibleNotifications = React.useMemo(() => {
    if (!Array.isArray(notifications)) {
      return [];
    }

    return notifications
      .filter(isPopupEnabled)
      .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [notifications]);

  const count = visibleNotifications.length;

  const latestNotification = count > 0 ? visibleNotifications[count - 1] : null;

  /*
   * IMPORTANT:
   *
   * The timer duration is captured once when the popup appears.
   * Later notification arrivals cannot restart the countdown.
   */
  const autoCloseMsRef = React.useRef(null);

  if (autoCloseMsRef.current === null) {
    autoCloseMsRef.current = getConfiguredAutoCloseMs(latestNotification);
  }

  const autoCloseMs = autoCloseMsRef.current ?? DEFAULT_AUTO_CLOSE_MS;

  /*
   * ---------------------------------------------------------
   * TIMER REFS
   * ---------------------------------------------------------
   *
   * We keep the actual timer state in refs so the animation
   * loop never sees stale hover/expanded state.
   */

  const animationFrameRef = React.useRef(null);

  const remainingMsRef = React.useRef(autoCloseMs);

  const lastFrameTimeRef = React.useRef(null);

  const pauseRequestedRef = React.useRef(false);

  const closingRef = React.useRef(false);

  const timerStartedRef = React.useRef(false);

  /*
   * ---------------------------------------------------------
   * PROGRESS
   * ---------------------------------------------------------
   */

  const [progress, setProgress] = React.useState(autoCloseMs);

  /*
   * ---------------------------------------------------------
   * TIMER PAUSE / RESUME
   * ---------------------------------------------------------
   *
   * pauseRequestedRef is the real source of truth for the
   * animation loop. This avoids the stale-closure problem that
   * caused hover pause not to work when the effect ran only once.
   */

  const hoverRef = React.useRef(false);

  const pauseTimer = React.useCallback(() => {
    if (closingRef.current) {
      return;
    }

    hoverRef.current = true;
    pauseRequestedRef.current = true;
    lastFrameTimeRef.current = performance.now();
    setIsPaused(true);
  }, []);

  const resumeTimer = React.useCallback(() => {
    if (closingRef.current) {
      return;
    }

    hoverRef.current = false;
    pauseRequestedRef.current = false;
    lastFrameTimeRef.current = performance.now();
    setIsPaused(false);
  }, []);

  /*
   * ---------------------------------------------------------
   * CLOSE
   * ---------------------------------------------------------
   */

  const closePopup = React.useCallback(
    (withSwipeAnimation = false) => {
      if (closingRef.current) {
        return;
      }

      closingRef.current = true;

      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);

        animationFrameRef.current = null;
      }

      if (withSwipeAnimation) {
        setIsDismissing(true);
        setSwipeOffset(-window.innerHeight);

        window.setTimeout(() => {
          onClose?.();
        }, SWIPE_CLOSE_ANIMATION_MS);

        return;
      }

      onClose?.();
    },
    [onClose],
  );

  /*
   * ---------------------------------------------------------
   * AUTO CLOSE TIMER
   * ---------------------------------------------------------
   *
   * Runs ONCE when this popup instance mounts.
   *
   * It does NOT depend on notification count, hover state,
   * expanded state, or progress. New notifications therefore
   * never restart the countdown.
   */

  React.useEffect(() => {
    if (count === 0 || autoCloseMs <= 0) {
      return undefined;
    }

    if (timerStartedRef.current) {
      return undefined;
    }

    timerStartedRef.current = true;
    remainingMsRef.current = autoCloseMs;
    lastFrameTimeRef.current = performance.now();

    setProgress(autoCloseMs);

    const animationLoop = (currentTime) => {
      if (closingRef.current) {
        return;
      }

      const paused = hoverRef.current;

      if (paused) {
        lastFrameTimeRef.current = currentTime;

        animationFrameRef.current = window.requestAnimationFrame(animationLoop);

        return;
      }

      const lastTime = lastFrameTimeRef.current ?? currentTime;

      const elapsed = Math.max(currentTime - lastTime, 0);

      lastFrameTimeRef.current = currentTime;

      remainingMsRef.current = Math.max(remainingMsRef.current - elapsed, 0);

      setProgress(remainingMsRef.current);

      if (remainingMsRef.current <= 0) {
        setIsPaused(false);
        closePopup(false);
        return;
      }

      animationFrameRef.current = window.requestAnimationFrame(animationLoop);
    };

    animationFrameRef.current = window.requestAnimationFrame(animationLoop);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);

        animationFrameRef.current = null;
      }
    };

    // This effect intentionally runs only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ---------------------------------------------------------
   * HOVER
   * ---------------------------------------------------------
   */

  const handleMouseEnter = React.useCallback(() => {
    pauseTimer();
  }, [pauseTimer]);

  const handleMouseLeave = React.useCallback(() => {
    resumeTimer();
  }, [resumeTimer]);

  /*
   * ---------------------------------------------------------
   * EXPAND / COLLAPSE
   * ---------------------------------------------------------
   */

  const handleToggleExpanded = React.useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  /*
   * ---------------------------------------------------------
   * SWIPE / DRAG UP TO DISMISS
   * ---------------------------------------------------------
   *
   * Uses Pointer Events so the same gesture works with:
   * - touch screen
   * - stylus
   * - mouse drag on desktop
   *
   * A normal click is NOT treated as a swipe.
   * The popup follows the pointer while dragging upward.
   */

  const swipeStartRef = React.useRef(null);
  const swipeTrackingRef = React.useRef(false);
  const swipeMovedRef = React.useRef(false);
  const swipeDetectedRef = React.useRef(false);

  const handlePointerDown = React.useCallback(
    (event) => {
      // Swipe is intentionally disabled while the dropdown is open.
      // This keeps normal dropdown interaction completely independent
      // from the swipe gesture.
      if (expanded || closingRef.current) {
        return;
      }

      swipeStartRef.current = {
        x: event.clientX,
        y: event.clientY,
      };

      swipeTrackingRef.current = true;
      swipeMovedRef.current = false;
      swipeDetectedRef.current = false;
    },
    [expanded],
  );

  const handlePointerMove = React.useCallback((event) => {
    if (
      !swipeTrackingRef.current ||
      !swipeStartRef.current ||
      closingRef.current
    ) {
      return;
    }

    const deltaX = event.clientX - swipeStartRef.current.x;
    const deltaY = event.clientY - swipeStartRef.current.y;

    const distance = Math.hypot(deltaX, deltaY);

    // Small movement must remain a normal click.
    if (distance < 8) {
      return;
    }

    const isMostlyVertical = Math.abs(deltaY) > Math.abs(deltaX) * 1.2;

    if (deltaY < 0 && isMostlyVertical) {
      swipeMovedRef.current = true;

      // Only prevent browser gesture handling after we have
      // actually identified an upward swipe.
      event.preventDefault();

      setIsSwiping(true);
      setSwipeOffset(Math.max(deltaY, -SWIPE_MAX_DRAG));
    }
  }, []);

  const handlePointerUp = React.useCallback(
    (event) => {
      if (!swipeTrackingRef.current || !swipeStartRef.current) {
        return;
      }

      const deltaX = event.clientX - swipeStartRef.current.x;
      const deltaY = event.clientY - swipeStartRef.current.y;

      const isUpwardSwipe =
        deltaY <= -SWIPE_DISMISS_THRESHOLD &&
        Math.abs(deltaY) > Math.abs(deltaX) * 1.2;

      swipeTrackingRef.current = false;
      swipeStartRef.current = null;

      if (isUpwardSwipe) {
        // This was a real swipe, not a click.
        swipeMovedRef.current = true;
        swipeDetectedRef.current = true;

        setIsSwiping(false);
        closePopup(true);
        return;
      }

      // It was only a small drag or a normal click.
      // IMPORTANT: do NOT suppress the next click here.
      // This is what keeps first-click / second-click dropdown
      // behaviour working after a swipe attempt.
      swipeMovedRef.current = false;
      swipeDetectedRef.current = false;
      setIsSwiping(false);
      setSwipeOffset(0);
    },
    [closePopup],
  );

  const handlePointerCancel = React.useCallback(() => {
    swipeTrackingRef.current = false;
    swipeStartRef.current = null;
    swipeMovedRef.current = false;
    swipeDetectedRef.current = false;

    setIsSwiping(false);
    setSwipeOffset(0);
  }, []);

  /*
   * ---------------------------------------------------------
   * CLOSE BUTTON
   * ---------------------------------------------------------
   */

  const handleCloseClick = React.useCallback(
    (event) => {
      event.stopPropagation();
      closePopup(false);
    },
    [closePopup],
  );

  /*
   * ---------------------------------------------------------
   * EMPTY STATE
   * ---------------------------------------------------------
   */

  if (count === 0) {
    return null;
  }

  /*
   * ---------------------------------------------------------
   * DISPLAY DATA
   * ---------------------------------------------------------
   */

  const latestModuleName = getModuleName(latestNotification);

  const latestActionName = getActionName(latestNotification);

  const latestMessage = getMessage(latestNotification);

  const progressPercent =
    autoCloseMs > 0
      ? Math.max(Math.min((progress / autoCloseMs) * 100, 100), 0)
      : 0;

  const cardTransform = isDismissing
    ? `translateY(-${window.innerHeight}px)`
    : `translateY(${swipeOffset}px)`;

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      sx={{
        width: "100%",
        touchAction: "none",
      }}
    >
      <Paper
        elevation={14}
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: (theme) => theme.palette.background.paper,

          transform: cardTransform,

          transition: isSwiping
            ? "none"
            : isDismissing
            ? `transform ${SWIPE_CLOSE_ANIMATION_MS}ms ease-in, opacity ${SWIPE_CLOSE_ANIMATION_MS}ms ease-in`
            : "transform 150ms ease-out, opacity 150ms ease-out",

          opacity: isDismissing ? 0 : 1,

          animation: "notificationGroupEnter 180ms ease-out",

          "@keyframes notificationGroupEnter": {
            from: {
              opacity: 0,
              transform: "translateY(-8px) scale(.99)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0) scale(1)",
            },
          },
        }}
      >
        {/* ===================================================
            HEADER
            =================================================== */}

        <Box
          onClick={(event) => {
            // A genuine upward swipe has already closed the popup.
            // Ignore only the synthetic click generated by that same
            // gesture; normal clicks before/after it are untouched.
            if (swipeDetectedRef.current) {
              swipeDetectedRef.current = false;
              event.preventDefault();
              event.stopPropagation();
              return;
            }

            handleToggleExpanded();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 1.6,
            cursor: "pointer",
            userSelect: "none",

            /* Prevent browser gesture handling while the popup
               itself is being swiped. */
            touchAction: expanded ? "auto" : "none",

            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          {/* Bell */}

          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              bgcolor: "primary.light",
              color: "primary.main",
            }}
          >
            <FeatherIcon icon="bell" width={20} height={20} />
          </Box>

          {/* Main text */}

          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {count}{" "}
                  {count === 1 ? "new notification" : "new notifications"}
                </Box>

                {/* Count */}

                <Box
                  sx={{
                    minWidth: 22,
                    height: 22,
                    px: 0.75,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {count}
                </Box>
              </Typography>
            </Stack>

            {/* Latest notification */}

            <Typography
              variant="body2"
              sx={{
                mt: 0.2,
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {latestActionName
                ? `${latestModuleName} • ${latestActionName} • `
                : `${latestModuleName} • `}
              {latestMessage}
            </Typography>
          </Box>

          {/* Close */}

          <IconButton
            size="small"
            onClick={handleCloseClick}
            aria-label="Close notifications"
          >
            <FeatherIcon icon="x" width={18} height={18} />
          </IconButton>

          {/* Expand */}

          <FeatherIcon
            icon={expanded ? "chevron-up" : "chevron-down"}
            width={18}
            height={18}
          />
        </Box>

        {/* ===================================================
            EXPANDED CONTENT
            =================================================== */}

        <Collapse in={expanded} timeout={180}>
          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {[...visibleNotifications].reverse().map((item, index) => {
              const moduleName = getModuleName(item);

              const actionName = getActionName(item);

              const message = getMessage(item);

              const createdDate = item?.createdDate ?? item?.CreatedDate;

              return (
                <Box
                  key={item?.id || item?.Id || `notification-${index}`}
                  sx={{
                    px: 1.75,
                    py: 1.15,
                    borderBottom:
                      index < visibleNotifications.length - 1
                        ? "1px solid"
                        : "none",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    {/* Item icon */}

                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flex: "0 0 auto",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "action.hover",
                        color: "text.secondary",
                      }}
                    >
                      <FeatherIcon icon="bell" width={14} height={14} />
                    </Box>

                    {/* Item content */}

                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 700,
                        }}
                      >
                        {actionName
                          ? `${moduleName} • ${actionName}`
                          : moduleName}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.2,
                          color: "text.secondary",
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                        }}
                      >
                        {message}
                      </Typography>

                      {createdDate && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mt: 0.35,
                          }}
                        >
                          {(() => {
                            const date = new Date(createdDate);

                            return Number.isNaN(date.getTime())
                              ? String(createdDate)
                              : date.toLocaleString();
                          })()}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Collapse>

        {/* ===================================================
            COUNTDOWN PROGRESS BAR
            =================================================== */}

        <Box
          sx={{
            position: "relative",
            height: 4,
            bgcolor: "action.hover",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progressPercent}%`,
              bgcolor: "#ff7043",
              transition: isSwiping ? "none" : "width 50ms linear",
            }}
          />
        </Box>

        {/* ===================================================
            STATUS / SWIPE HINT
            =================================================== */}

        <Box
          sx={{
            px: 1.5,
            py: 0.55,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            borderTop: "1px solid",
            borderColor: "divider",
            minHeight: 27,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isPaused ? "#ff7043" : "text.secondary",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textAlign: "center",
            }}
          >
            <FeatherIcon
              icon={isPaused ? "pause-circle" : "arrow-up"}
              width={13}
              height={13}
            />

            {isPaused
              ? expanded
                ? "Expanded — timer paused"
                : "Hovering — timer paused"
              : "Swipe up to dismiss"}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default EpoNotificationToast;
